import React, { useEffect, useState, useRef } from 'react'
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import EmojiPicker from "emoji-picker-react";
import ToolbarTooltipButton from "../../../components/common/ToolbarTooltipButton";
import undo from "../../../assets/tips/undo.svg";
import redo from "../../../assets/tips/redo.svg";
import smile from "../../../assets/tips/smile.svg";
import delet from "../../../assets/tips/delete.svg";
import edit from "../../../assets/tips/edit.svg";
import apply from "../../../assets/tips/apply.svg";
import copyIcon from "../../../assets/posts/copy.svg";

const FirstComment = ({ canIncludeFirstComment, tipsText }) => {
    const { t } = useTranslation();
    const editorRef = useRef(null);
    const savedRangeRef = useRef(null);

    const [commentFontStyle, setcommentFontStyle] = useState("Font Styles");
    const [includeFirstComment, setIncludeFirstComment] = useState(false);
    const [commentText, setCommentText] = useState("");
    const [appliedComment, setAppliedComment] = useState("");
    const [isEditingComment, setIsEditingComment] = useState(false);

    const [commentHistory, setCommentHistory] = useState([]);
    const [commentRedoStack, setCommentRedoStack] = useState([]);
    const [selectedEmoji, setSelectedEmoji] = useState(null);
    const [isSmileDropdownOpen, setIsSmileDropdownOpen] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!tipsText?.trim()) {
            setIncludeFirstComment(false);
            setCommentText("");
            setAppliedComment("");
            setIsEditingComment(false);
        }
    }, [tipsText]);

    useEffect(() => {
        if (editorRef.current && isEditingComment) {
            editorRef.current.innerHTML = appliedComment || "";
            setCommentText(appliedComment || "");
        }
    }, [isEditingComment]);



    const handleCommentChange = () => {
        const html = editorRef.current.innerHTML;

        setCommentHistory(prev => [...prev, html]);   // store current html
        setCommentText(html);
        setCommentRedoStack([]);
    };

    const handleCommentUndo = () => {
        if (!commentHistory.length) return;

        const prev = commentHistory[commentHistory.length - 1];

        setCommentRedoStack(r => [editorRef.current.innerHTML, ...r]);

        setCommentHistory(h => h.slice(0, -1));

        editorRef.current.innerHTML = prev;
        setCommentText(prev);
    };

    const handleCommentRedo = () => {
        if (!commentRedoStack.length) return;

        const next = commentRedoStack[0];

        setCommentHistory(h => [...h, editorRef.current.innerHTML]);

        setCommentRedoStack(r => r.slice(1));

        editorRef.current.innerHTML = next;
        setCommentText(next);
    };

    const handleFontStyleChange = (e) => {
        const style = e.target.value;
        setcommentFontStyle(style);

        if (!editorRef.current) return;

        editorRef.current.focus();

        switch (style) {
            case "bold":
                document.execCommand("bold");
                break;
            case "italic":
                document.execCommand("italic");
                break;
            case "bold-italic":
                document.execCommand("bold");
                document.execCommand("italic");
                break;
            case "normal":
                document.execCommand("removeFormat");
                break;
            default:
                break;
        }
    };

    const saveSelection = () => {
        const selection = window.getSelection();
        if (!selection.rangeCount) return;
        savedRangeRef.current = selection.getRangeAt(0);
    };

    const handleApplyComment = () => {
        if (!editorRef.current?.innerHTML.trim()) return;
        setAppliedComment(editorRef.current.innerHTML);
        setIsEditingComment(false);
    };


    const handleEditComment = () => {
        setIsEditingComment(true);
    };

    const handleRemoveComment = () => {
        setAppliedComment("");
        setCommentText("");
        setIncludeFirstComment(false);
        setIsEditingComment(false);
        setSelectedEmoji(null);
        setIsSmileDropdownOpen(false);
        setcommentFontStyle("Font Styles");
    };

    const handleDelete = () => {
        editorRef.current.innerHTML = "";
        setCommentText("");
        setCommentHistory([]);
        setCommentRedoStack([]);
        setSelectedEmoji(null);
        setIsSmileDropdownOpen(false);
        setcommentFontStyle("Font Styles");
    };

    const handleCopyText = async () => {
        if (!tipsText || !editorRef.current) return;

        try {
            await navigator.clipboard.writeText(
                editorRef.current.innerText
            );

            setCopied(true);

            setTimeout(() => {
                setCopied(false);
            }, 2000);

        } catch (err) {
            console.error("Copy failed", err);
        }
    };

    return (
        <div id="section-post-comment" className='text-black-70 bg-black-20 border border-black-40 rounded-lg mt-4 mb-4'>
            <label className="flex items-center text-lg px-6 pt-6 pb-2">
                <input
                    type="checkbox"
                    checked={includeFirstComment}
                    disabled
                    onChange={(e) => {
                        setIncludeFirstComment(e.target.checked);
                        setIsEditingComment(e.target.checked);
                    }}
                    className="h-4 w-4 border-black-40 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <span className="ml-2">{t("insights.check")}</span>
            </label>
            <div className='px-6 mb-4'>

            <span className="text-xl bg-Brand-lemon-100 text-black-800 px-2 rounded w-fit">
                {t("updates.newFeature")}
            </span>
            </div>

            {includeFirstComment && isEditingComment && (
                <>

                    <div className="space-x-2 mb-4 border-t border-b bg-gray-50 h-auto md:h-[294px] relative">
                        <div className="flex flex-wrap items-center gap-2 p-2 bg-black-20 rounded-tr-lg rounded-tl-lg w-full relative z-10">
                            <ToolbarTooltipButton
                                label={t("buttons.undo")}
                                onClick={handleCommentUndo}
                                disabled={!commentHistory.length}
                            >
                                <img src={undo} alt="" className="w-4 h-4" />
                            </ToolbarTooltipButton>

                            <ToolbarTooltipButton
                                label={t("buttons.redo")}
                                onClick={handleCommentRedo}
                                disabled={!commentRedoStack.length}
                            >
                                <img src={redo} alt="redo" className="w-4 h-4" />
                            </ToolbarTooltipButton>

                            {/* Font Style */}
                            <ToolbarTooltipButton
                              label={t("buttons.fontStyle")}
                              noButton={true}
                              wrapperClassName="relative inline-flex mt-2 flex-1 min-w-0"
                            >
                              <select
                                value={commentFontStyle}
                                onChange={handleFontStyleChange}
                                className="h-10 sm:h-11 w-full sm:w-auto sm:min-w-[220px] md:min-w-[380px] px-2 border border-black-600 rounded bg-white text-black-500 text-sm cursor-pointer"
                              >
                                <option>{t("insights.fontStyle")}</option>
                                <option value="normal">Normal</option>
                                <option value="bold">Bold</option>
                                <option value="italic">Italic</option>
                                <option value="bold-italic">Bold Italic</option>
                              </select>
                            </ToolbarTooltipButton>

                            {/* Emoji */}
                            <div className="relative">
                                <ToolbarTooltipButton
                                    label={t("buttons.emojis")}
                                    onClick={() => setIsSmileDropdownOpen((prev) => !prev)}
                                >
                                    {selectedEmoji ? selectedEmoji : (
                                        <img src={smile} alt="" className="w-5 h-5" />
                                    )}
                                </ToolbarTooltipButton>

                                {isSmileDropdownOpen && (
                                    <div className="absolute top-14 right-0 z-50">
                                        <EmojiPicker
                                            onEmojiClick={(emojiData) => {
                                                editorRef.current.focus();

                                                const selection = window.getSelection();

                                                if (savedRangeRef.current) {
                                                    selection.removeAllRanges();
                                                    selection.addRange(savedRangeRef.current);
                                                }

                                                const range = selection.getRangeAt(0);
                                                range.deleteContents();

                                                const textNode = document.createTextNode(emojiData.emoji);
                                                range.insertNode(textNode);

                                                // move cursor after emoji
                                                range.setStartAfter(textNode);
                                                range.collapse(true);

                                                selection.removeAllRanges();
                                                selection.addRange(range);

                                                // update state
                                                const html = editorRef.current.innerHTML;
                                                setCommentText(html);
                                                setSelectedEmoji(emojiData.emoji);
                                                setIsSmileDropdownOpen(false);

                                                saveSelection();
                                            }}
                                            height={350}
                                            width={300}
                                        />
                                    </div>
                                )}
                            </div>

                            <ToolbarTooltipButton
                                label={t("buttons.clear")}
                                onClick={handleDelete}
                                className="w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center border border-black-600 rounded bg-black-0 hover:bg-black-20"
                            >
                                <img src={delet} alt="" className="w-4 h-4" />
                            </ToolbarTooltipButton>
                        </div>
                        <div className="p-2 h-[100px] relative border-t">
                            <div
                                ref={editorRef}
                                contentEditable
                                suppressContentEditableWarning
                                onInput={() => {
                                    handleCommentChange();
                                    saveSelection();
                                }}
                                onKeyUp={saveSelection}
                                onMouseUp={saveSelection}
                                className="w-full h-full bg-transparent resize-none focus:outline-none text-gray-700 whitespace-pre-wrap"
                            ></div>

                            <div className="flex justify-end">
                                <ToolbarTooltipButton
                                    label={t("buttons.copy")}
                                    onClick={handleCopyText}
                                    className="w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center border border-black-600 rounded bg-black-0 hover:bg-black-20">
                                    <img src={copyIcon} alt="copy" className="w-4 h-4" />
                                </ToolbarTooltipButton>
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-end m-2">
                        <button
                            disabled={!commentText.trim()}
                            onClick={handleApplyComment}
                            className="flex items-center gap-2 px-4 py-2 bg-secondary-400 hover:bg-secondary-500 border border-secondary-400 focus:border-primary-400 active:bg-secondary-600 text-black-900 rounded text-lg font-bold  transition-colors duration-200 disabled:opacity-40"
                        >
                            Apply
                            <img src={apply} alt="apply" className='w-6 h-6' />
                        </button>
                    </div>
                </>
            )}
            {includeFirstComment && !isEditingComment && appliedComment && (
                <div className="border-t">

                    {/* Preview Comment */}
                    <div
                        className="relative bg-white border-t border-b border-black-40 p-3 text-gray-700 whitespace-pre-wrap"
                    >
                        <div
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(appliedComment) }}
                        />
                        <div className="absolute top-[40%] right-3 ">

                            <button
                                onClick={handleEditComment}
                                className="flex items-center gap-2 text-sm font-semibold text-secondary-500 hover:text-secondary-600"
                            >
                                <img src={edit} alt="edit" />
                            </button>
                        </div>
                    </div>

                    {/* Remove Button */}
                    <div className="flex justify-end m-2">
                        <button
                            onClick={handleRemoveComment}
                            className="flex items-center gap-2 px-4 py-2 bg-secondary-400 hover:bg-secondary-500 border border-secondary-400 focus:border-primary-400 active:bg-secondary-600 text-black-900 rounded text-lg font-bold  transition-colors duration-200"
                        >
                            Remove comment
                            <img src={delet} alt="delet" />
                        </button>
                    </div>

                </div>
            )}

            {copied && (
                <div className="
                    fixed bottom-6 right-6
                    bg-black-800 text-white
                    px-4 py-2
                    rounded-lg
                    shadow-lg
                    z-50
                    animate-fade-in
                ">
                    Copied to clipboard
                </div>
            )}
        </div>
    )
}

export default FirstComment